import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back, Paul.");
        navigate("/admin");
      } else {
        await api.post("/auth/forgot-password", { email });
        toast.success("If that email exists, a reset link has been sent.");
        setMode("login");
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="light-scope flex items-center justify-center px-5 py-16 min-h-screen">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#58635C] hover:text-[#0A0D0B] mb-10">
          <ArrowLeft size={15} /> Back to site
        </Link>
        <div className="font-display text-4xl tracking-tight text-[#0A0D0B]">Grafton<span className="text-[#B89874]">.</span></div>
        <p className="mt-2 text-[11px] uppercase tracking-[0.28em] text-[#B89874]">Admin Portal</p>

        <form onSubmit={submit} data-testid="login-form" className="mt-10 space-y-5">
          <div className="flex flex-col gap-2">
            <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Email</label>
            <input
              data-testid="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="border border-[#E5E1D8] px-4 py-3 text-[#0A0D0B] focus:border-[#B89874] outline-none transition-colors"
            />
          </div>
          {mode === "login" && (
            <div className="flex flex-col gap-2">
              <label className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border border-[#E5E1D8] px-4 py-3 text-[#0A0D0B] focus:border-[#B89874] outline-none transition-colors"
              />
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0D0B] text-white px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] hover:bg-[#233027] transition-colors disabled:opacity-60"
          >
            {busy && <Loader2 size={15} className="animate-spin" />}
            {mode === "login" ? "Sign In" : "Send Reset Link"}
          </button>
          <button
            type="button"
            data-testid="toggle-forgot"
            onClick={() => setMode(mode === "login" ? "forgot" : "login")}
            className="w-full text-center text-sm text-[#58635C] hover:text-[#0A0D0B] underline"
          >
            {mode === "login" ? "Forgot your password?" : "Back to sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}
